import { Component, inject } from '@angular/core';
import { CbButtonModule, CbIconModule, CbModalModule } from '@chubb/ui-components';

import { Coverage } from '../../../interfaces/coverage.model';
import { Customer } from '../../../interfaces/customer.model';
import { PolicyData } from '../../../interfaces/policy-data.model';
import { ValidationError } from '../../../interfaces/validation-error.model';
import { BaseCoverageWidgetComponent } from '../../../shared/components/base-coverage.widget.component';
import { Actions } from '../../../shared/enums/action.enum';
import { BannerAlertType } from '../../../shared/enums/alert-icon.enum';
import { MessageBannerService } from '../../../shared/services/message-banner.service';

@Component({
  selector: 'app-remove-cover',
  imports: [CbIconModule, CbButtonModule, CbModalModule],
  templateUrl: './remove-cover.component.html',
  styleUrl: './remove-cover.component.scss',
})
export class RemoveCoverComponent extends BaseCoverageWidgetComponent {
  open = false;
  isRemoving = false;
  customerName: string | undefined;
  coverageRefDescription: string | undefined;
  canDelete: boolean = true;
  cantRemoveMessage: string = '';

  protected readonly _alertBanner: MessageBannerService = inject(MessageBannerService);

  handleModal() {
    this.coverageRefDescription = this._baseGetValByCode('coverageCode', this.coverage().code);
    this.customerName = this.getCustomerNameByCoverage();

    if (!this.canRemoveCoverage()) {
      this.canDelete = false;
    }

    this.open = !this.open;
  }

  /**
   * Removes a coverage from the policy if allowed, updates the alert banner,
   * collapses the corresponding accordion panel, recalculates the premium,
   * and resets the sum insured baseline.
   *
   * @returns {void}
   */
  async remove() {
    this.handleModal();
    this.isRemoving = true;
    if (!this.sharedPolicyData?.coverages || !this.sharedPolicyData?.customers) {
      this.isRemoving = false;
      return;
    }

    // Find the customer object index based on customerNumber
    const customerIndex = this.sharedPolicyData.customers.findIndex(
      (c: Customer) => c.number === this.coverage().customerNumber,
    );

    if (customerIndex === -1) {
      console.warn('Customer not found!');
      this.isRemoving = false;
      return;
    }

    // Find the coverage with that customerNumber
    const coverageIndex = this.sharedPolicyData.coverages.findIndex(
      (c: Coverage) =>
        c.customerNumber === this.coverage().customerNumber &&
        c.code === this.coverage().code &&
        c.group === this.coverage().group,
    );
    if (coverageIndex !== -1) {
      const policyBeforeRemoval = structuredClone(this.sharedPolicyData);
      this.sharedPolicyData?.coverages?.splice(coverageIndex, 1);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      await this._baseTriggerPremiumCalculation().catch(error => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        this._utilsService
          .reAttemptIfTokenExpiry(() => this._baseTriggerPremiumCalculation())
          .catch((reAttemptError: string) => {
            this.showError(
              `Error performing premium calculation: An error has occurred, please call the Chubb Life team on 0508 464 999 for assistance.`,
            );
          });
      });
      const removeCoverErrors =
        this.sharedPolicyData.evaluationResponse?.errors?.filter(error =>
          error.message.startsWith('Remove Cover - '),
        ) ?? [];
      if (removeCoverErrors.length > 0) {
        this.sharedPolicyData = policyBeforeRemoval;
        //update sharedPolicyData response to point to this coverage
        this._baseDataService.updateData(this._getUpdatedRemoveCoverErrorPolicy(removeCoverErrors));
      } else {
        this._onRemoveCoverSuccess(coverageIndex);
      }
    } else {
      console.warn('Coverage not found in collection!');
    }

    this.isRemoving = false;
  }

  private _onRemoveCoverSuccess(coverageIndex: number) {
    this._alertBanner.setMessage({
      type: BannerAlertType.success,
      message: `${this.coverageRefDescription} has been removed successfully.`,
      action: Actions.REMOVE_COVER,
    });
    this.removeCoverAccordion(coverageIndex);
    this._alertBanner.showBanner();
    this._baseDataService.updateData(this.sharedPolicyData);
    // Reset stored sumInsured after premium calculation
    this._initialiseSumInsuredBaseline();
  }

  private _getUpdatedRemoveCoverErrorPolicy(coverErrors: ValidationError[]): PolicyData {
    let newPolicy = structuredClone(this.sharedPolicyData);
    const newErrors: ValidationError[] = [];

    coverErrors.forEach((error: ValidationError) => {
      if (error.message) {
        newErrors.push({
          path: [`customers[${this.customerIndex()}]`, `coverages[${this.coverageIndex()}]`],
          message: error.message,
        }); // Correctly push message into errorMessages
      }
    });
    newPolicy!.evaluationResponse = {};
    newPolicy!.evaluationResponse!.errors = newErrors;

    return newPolicy!;
  }

  /**
   * Updates the expanded accordions state to remove the given coverage index for the relevant customer.
   *
   * @param {number} [coverageIndex] - The index of the coverage to remove from the expanded accordions.
   * @returns {void}
   */
  removeCoverAccordion(coverageIndex?: number) {
    const accordionStates = structuredClone(this._baseDataService.get('expandedCoverages'));
    if (coverageIndex != null) {
      const customersAccordions = accordionStates.find(
        (state: { customer: number; accordions: number[] }) =>
          state.customer.toString() === this.sharedPolicyData?.customers![this.customerIndex()].number,
      );
      if (customersAccordions) {
        customersAccordions.accordions = customersAccordions.accordions.filter(
          (index: number) => index !== coverageIndex,
        );
      }
      this._baseDataService.set('expandedCoverages', accordionStates);
    }
  }

  /**
   * Called from on init.
   * Determines if the coverage can be removed for the selected customer based on whether the customer is not left with no coverages if this one is deleted.
   *
   * @returns {boolean} True if the coverage can be removed, false otherwise.
   */
  private canRemoveCoverage(): boolean {
    if (!this.coverage().customerNumber || !this.sharedPolicyData?.coverages) {
      return false; // cannot remove if data missing
    }

    const customerCoverages = this.sharedPolicyData?.coverages.filter(
      (c: Coverage) => c.customerNumber === this.coverage().customerNumber,
    );

    // Allow removal only if more than one coverage exists
    return customerCoverages.length > 1;
  }

  /**
   * Retrieves the customer's full name based on the current coverage's customer number.
   *
   * @returns {string | undefined} The customer's full name, or undefined if not found.
   */
  private getCustomerNameByCoverage(): string | undefined {
    if (!this.coverage().customerNumber || !this.sharedPolicyData?.customers) {
      return undefined;
    }
    const customer = this.sharedPolicyData?.customers.find(
      (c: Customer) => c.number === this.coverage().customerNumber,
    );
    if (customer) {
      const firstName = customer.firstName ?? '';
      const lastName = customer.lastName ?? '';
      return `${firstName} ${lastName}`.trim();
    }
    return undefined;
  }
}
